/**
 * packages/shared/src/time.ts
 * -------------------------------------------------------
 * Purpose:
 *   Shared application time helpers.
 *
 * High-level data flow:
 *   - Both the API and the web app import the same timezone constant so
 *     date filters and displayed timestamps stay aligned.
 *   - For now the timezone is hard-coded, but this module gives us a
 *     single seam to replace with org/user settings later.
 */
/** Default application timezone (hard-coded for now). */
export const APP_TIME_ZONE = 'America/Los_Angeles';
/** Human-readable label for the default timezone. */
export const APP_TIME_ZONE_LABEL = 'Pacific Time';
function toDate(input) {
    return input instanceof Date ? input : new Date(input);
}
/**
 * Format a timestamp using the application's default timezone.
 * Includes a short timezone marker so non-local rendering is obvious.
 */
export function formatDateTimeInAppTimeZone(input, locale, options = {}) {
    return new Intl.DateTimeFormat(locale, {
        timeZone: APP_TIME_ZONE,
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short',
        ...options,
    }).format(toDate(input));
}
/**
 * Format a date-like value as a calendar date without shifting the day across time zones.
 * This is useful for date-only fields that may be stored as midnight UTC.
 */
export function formatDateOnly(input, locale, options = {}) {
    return new Intl.DateTimeFormat(locale, {
        timeZone: 'UTC',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        ...options,
    }).format(toDate(input));
}
/**
 * Return a UTC-anchored Date whose calendar components match the same
 * instant in the app timezone.
 *
 * Treat the returned value as a calendar-only helper. It is intentionally
 * anchored at noon UTC so adding/subtracting days stays stable across DST.
 */
export function getCalendarDateInAppTimeZone(input = new Date()) {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: APP_TIME_ZONE,
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
    }).formatToParts(toDate(input));
    const year = Number(parts.find((part) => part.type === 'year')?.value ?? 0);
    const month = Number(parts.find((part) => part.type === 'month')?.value ?? 0);
    const day = Number(parts.find((part) => part.type === 'day')?.value ?? 0);
    return new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
}
/** Add or subtract calendar days from a UTC-anchored date helper. */
export function addCalendarDays(date, days) {
    const next = new Date(date);
    next.setUTCDate(next.getUTCDate() + days);
    return next;
}
/** Format a UTC-anchored calendar helper as a human-friendly date string. */
export function formatCalendarDate(date, locale) {
    return new Intl.DateTimeFormat(locale, {
        timeZone: 'UTC',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    }).format(date);
}
